import type { Optional } from '@festina-life/core'
import { useMemo } from 'react'

import { useLayoutContext } from '../../context/layoutContext'
import { isModelMetaType, rootType } from '../../types/metaModel'
import {
  isNotRelevantField,
  isSimpleTemplateField,
  isSubTemplatedField,
  type SimpleTemplateField,
  type SubTemplateValueSetupField,
  type TemplateField,
} from '../../types/templateModel'
import type { SpecialCasedSubTemplatedValueSetup } from '../specialCasedSubTemplatedViewers/types'
import { useSpecialSubTemplatedTemplatedConfigurations } from '../specialCasedSubTemplatedViewers/useSpecialSubTemplatedTemplatedConfigurations'

type CategorizedFields = {
  composite: SubTemplateValueSetupField[]
  simple: SimpleTemplateField[]
  specialCased: SpecialCasedSubTemplatedValueSetup[]
  specialCasedType?: Optional<string>
}

/**
 * Utility hook used for categorising the list of simple and composite field to be shown on a TemplateFieldsTable.
 */
export const useCategorizeTemplateFields = (fields: TemplateField[]) => {
  const specialConfigurations = useSpecialSubTemplatedTemplatedConfigurations()
  const {
    preferences: {
      templates: {
        isShowingEmptyValues,
        isShowingNativeView,
        isShowingNotRelevantFields,
      },
    },
  } = useLayoutContext()
  const configurations = useSpecialSubTemplatedTemplatedConfigurations()

  const specialSubTemplatedModelTypes = Object.keys(specialConfigurations)

  const visibleFields = useMemo(
    () =>
      fields.filter(
        (field) =>
          (isShowingEmptyValues || !!field.value) &&
          (isShowingNotRelevantFields || !isNotRelevantField(field)),
      ),
    [fields, isShowingEmptyValues, isShowingNotRelevantFields],
  )

  return useMemo(() => {
    const categorized = visibleFields.reduce<CategorizedFields>(
      (acc, field) => {
        if (isSubTemplatedField(field)) {
          const fieldRootType = isModelMetaType(field.originalPropertyType)
            ? rootType(field.originalPropertyType)
            : undefined
          const isSpeciallyRenderedType =
            field.originalPropertyType &&
            fieldRootType &&
            specialSubTemplatedModelTypes.includes(fieldRootType)

          if (!isSpeciallyRenderedType || isShowingNativeView) {
            acc.composite.push(field)
          } else {
            const rootTypeConfiguration = configurations[fieldRootType]
            const variants = field.value?.variants ?? []

            // TODO: Apply sorting to the variants. For CcStep they should be sorted by priority
            acc.specialCased = acc.specialCased.concat(
              variants.map((variant) => ({
                field,
                subTemplateIdentifier: variant.subTemplateIdentifier,
              })),
            )
            acc.specialCasedType = fieldRootType

            if (rootTypeConfiguration.sort) {
              acc.specialCased.sort(rootTypeConfiguration.sort)
            }
          }
        } else if (isSimpleTemplateField(field)) {
          acc.simple.push(field)
        }

        return acc
      },
      { simple: [], composite: [], specialCased: [] },
    )

    // Fields are sorted in order for them to be rendered in a stable position.

    categorized.simple.sort((simpleA, simpleB) =>
      simpleA.name.localeCompare(simpleB.name, undefined, {
        sensitivity: 'base',
      }),
    )

    categorized.composite.sort((compositeA, compositeB) =>
      compositeA.name.localeCompare(compositeB.name, undefined, {
        sensitivity: 'base',
      }),
    )

    categorized.specialCased.sort((specialCasedA, specialCasedB) =>
      specialCasedA.field.name.localeCompare(
        specialCasedB.field.name,
        undefined,
        {
          sensitivity: 'base',
        },
      ),
    )

    return categorized
  }, [
    configurations,
    isShowingNativeView,
    specialSubTemplatedModelTypes,
    visibleFields,
  ])
}
